/// <amd-module name="Wit/BoardsHub/KanbanBoard/IKanbanBoardService" />
import { IIterationData, ITeamFieldData, ITeamSettings } from "Agile/Contracts/Common";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableValue, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { traceException } from "VSS/Platform/Trace";
import { toLookup } from "VSS/Platform/Util/Array";
import { IIdentity } from "VSSUI/IdentityPicker";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { FILTER_CHANGE_EVENT, Filter, IFilter } from "VSSUI/Utilities/Filter";
import { populateAnnotationSettings } from "Wit/BoardsHub/Common/AnnotationUtils";
import { BoardsWorkItem } from "Wit/BoardsHub/Common/BoardsWorkItem";
import {
    BacklogLevelConfiguration,
    BoardColumn,
    BoardColumnType,
    BoardRow,
    BoardSettings,
    CardRule
} from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { ColumnsValidator, IColumnsValidator } from "Wit/BoardsHub/Common/ColumnsValidator";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import { IBoardsWorkItem, IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { BoardSettingsConstants, WorkItemSignalREventType } from "Wit/BoardsHub/Common/KanbanBoardConstants";
import {
    BoardColumnLayout,
    BoardData,
    BoardFieldDefinition,
    BoardFieldNames,
    BoardLayout,
    BoardRowLayout,
    CardLayout,
    IBoardSettingsData,
    WorkItemSource,
    WorkItemTypeColorAndIconMap
} from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { divyCardRules, getCardStylesFromCardRules } from "Wit/BoardsHub/Common/StylesUtils";
import { ISwimlanesValidator, SwimlanesValidator } from "Wit/BoardsHub/Common/SwimlanesValidator";
import { IBoardUpdatePayload } from "Wit/BoardsHub/KanbanBoard/SignalR/Contracts";
import { IKanbanBoardSignalRService, IKanbanBoardSignalRServiceId } from "Wit/BoardsHub/KanbanBoard/SignalR/KanbanBoardSignalRService";
import { getFieldEntriesFromBoardFields } from "Wit/BoardsHub/KanbanBoard/Utils/FieldUtils";
import {
    ICardStyle,
    IClientWorkItem,
    getObservableWorkItemSorter,
    observableWorkItemClosedDateSorter
} from "Wit/ClientWorkItemService/IClientWorkItem";
import { ClientWorkItemServiceEvent, IClientWorkItemUpdatedEvent } from "Wit/ClientWorkItemService/IClientWorkItemService";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import * as WorkContracts from "Wit/Clients/Work/Contracts/Work";
import { BacklogConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { BoardFilterDataSource } from "Wit/Common/Controls/Boards/BoardFilterDataSource";
import { divyFields } from "Wit/Common/Controls/Boards/Card";
import { FieldLayout } from "Wit/Common/Controls/Boards/Interfaces";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { isClassificationPathEqualOrUnderRelative } from "Wit/Common/NodeHelpers";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { convertNodesToListBoxItems } from "Wit/Common/WorkItemControls/NodeUtils";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { AreaPathFilterProvider } from "Wit/Filter/AreaPathFilterProvider";
import { getAreaPathsMatchingLeaf } from "Wit/Filter/AreaPathHelper";
import { AssignedToFilterProvider } from "Wit/Filter/AssignedToFilterProvider";
import { FieldFilterProvider } from "Wit/Filter/FieldFilterProvider";
import { FilterManager, FilterState, FilterValue, IBoardFilterDataSource, IFilter as IFilterItemState } from "Wit/Filter/FilterManager";
import { IterationPathFilterProvider } from "Wit/Filter/IterationPathFilterProvider";
import { ParentFilterProvider } from "Wit/Filter/ParentFilterProvider";
import { TagsFilterProvider } from "Wit/Filter/TagsFilterProvider";
import { SubstringTextFilterProvider, TextFilterProvider } from "Wit/Filter/TextFilterProvider";
import { mapToFilterState } from "Wit/Filter/WorkItemFilter";
import { getBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { WorkItemTypeColorAndIcon } from "Wit/UI/WorkItemTypeIcon";
import { IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import * as React from "react";
import { InvalidBoardMessage } from "./Components/Util";
import { IAnnotationService } from "./IAnnotationService";
import { ISwimlaneRulesAutoMoveService } from "./ISwimlaneRulesAutoMoveService";
import { hasItem, isInProgress, isIncoming, isOutgoing } from "./Utils/BoardUtils";
import { ContainerNames } from "Wit/Messages/Components/BoardsMessages";

const BoardsDataProvider = "ms.vss-work-web.kanban-board-content-data-provider";
const WorkItemTypeColorIconDataProvider = "ms.vss-work-web.work-item-type-color-icon-data-provider";
const BacklogConfigurationDataProvider = "ms.vss-work-web.agile-backlog-configuration-data-provider";
const BacklogPermissionDataProvider = "ms.vss-work-web.backlog-permission-data-provider";
const WorkItemPermissionsDataProvider = "ms.vss-work-web.work-item-permissions-data-provider";

interface IAdvanceBacklogManagementPermission {
    hasPermission: boolean;
}

/**
 * These values are determined by the server's serialization code
 */
namespace BoardFieldNameKeys {
    export const ColumnField = "ColumnField";
    export const DoneField = "DoneField";
    export const RowField = "RowField";
}

export interface IKanbanBoardService extends IVssService {
    boardSettingsData: IReadonlyObservableValue<IBoardSettingsData | undefined>;
    boardLayout: IReadonlyObservableValue<BoardLayout | undefined>;
    isFiltering: IReadonlyObservableValue<boolean>;
    filter: IFilter;
    filterDataSource: IBoardFilterDataSource;

    readonly initialFilterState?: FilterState;
    /**
     * Return true if given column can be reordered, and if supplied, if the given item can be reorderd
     */
    canReorderInColumn(columnId?: string, itemId?: number, doneColumn?: boolean): boolean;
    /**
     * Get all rows (swimlanes) layouts
     */
    getRowsLayout(columnNameOrId?: string): BoardRowLayout[] | undefined;
    /**
     * Get a row layout given a row id
     */
    getRowLayout(rowNameOrId: string): BoardRowLayout | undefined;
    /**
     * Get the row that's displaying the work item
     */
    getWorkItemRowLayout(itemId: number): BoardRowLayout | undefined;
    /**
     * Expand or collapse all swimlanes on the board
     */
    expandOrCollapseAllSwimlanes(expand: boolean): void;
    /**
     * Get all columns layout
     */
    getColumnsLayout(): BoardColumnLayout[] | undefined;
    /**
     * Get a column layout given a column id and optionally a row id
     */
    getColumnLayout(columnId?: string, rowId?: string, isDone?: boolean): BoardColumnLayout | undefined;
    /**
     * Get the column that's displaying the work item
     */
    getWorkItemColumnLayout(itemId: number): BoardColumnLayout | undefined;
    /**
     * Boolean value if work items have been loaded into work item store or not
     */
    getWorkItemsLoaded(): IObservableValue<boolean>;
    /**
     * Load the next set of partial work items for the given column
     */
    loadNextPartialWorkItems(column: BoardColumnLayout, filter?: string): Promise<void>;
    /**
     * Reload board data after a context switch like team or backlog level changing
     * note: board data provider parses team and backlog data from URL, so these must be updated before reloading data
     */
    reloadBoardData(): Promise<void>;
    /**
     * Move an item to a new row/column.
     */
    moveItem(itemId: number, columnId: string, rowId?: string, doneColumn?: boolean, previousId?: number, nextId?: number): Promise<void>;
    /**
     * Update a field value on an item.
     */
    updateItem(itemId: number, fieldReferenceName: string, newValue: any): Promise<void>;
    /**
     * Update iteration Id and Iteration Path of element (and all of its unfinished children)
     * @param selectedWorkItem current item
     * @param iteration iteration
     */
    updateItemIteration(selectedWorkItem: IObservableBoardsWorkItem, iteration: IIterationData): Promise<void>;
    unwatchSignalR(): Promise<void>;
    /**
     * Remove an item from a column.
     */
    removeItem(itemId: number): void;
    getRowNameError(rowId: string): IReadonlyObservableValue<string | undefined>;
    renameRow(rowId: string, rowName: string): Promise<void>;

    getColumnNameError(columnId: string): IReadonlyObservableValue<string | undefined>;
    renameColumn(columnId: string, columnName: string): Promise<void>;

    reloadAnnotaionData(itemIds: number[], teamId: string): Promise<void>;
    getBacklogDefaultWorkItemTypeName(): Promise<string | undefined>;

    refreshBoardLayout(): void;
    hasPermissionToDeleteWorkItems(): ObservableValue<boolean>;
}

class KanbanBoardService extends VssService implements IKanbanBoardService {
    private backlogConfiguration: BacklogConfiguration | undefined;
    private permissionData: IAdvanceBacklogManagementPermission | undefined;
    private boardSettingsDataObservable = new ObservableValue<IBoardSettingsData | undefined>(undefined);
    private isFilteringObservable = new ObservableValue<boolean>(false);
    private boardFields?: BoardFieldNames;
    private boardLayoutObservable = new ObservableValue<BoardLayout | undefined>(undefined);
    private initialFilters?: FilterState = undefined;

    // contains expanded observables for incoming and outgoing columns and all swimlanes, mapped by guid
    private expandedObservables = new Map<string, IObservableValue<boolean>>();
    private cardStyles: ICardStyle[];
    private columnFilterManager: FilterManager;
    private filterManager: FilterManager;
    private projectId: string;
    private workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] = [];
    private workItemService: IBoardsWorkItemService;

    private idsForLastIncomingFilter: number[] = [];
    private idsForLastOutgoingFilter: number[] = [];
    private lastIncomingFiltersStr?: string;
    private lastOutgoingFiltersStr?: string;
    private lastIncomingFilteredPagedIndex = -1;
    private lastOutgoingFilteredPagedIndex = -1;
    private nextIncomingIndexToPage = -1;
    private nextOutgoingIndexToPage = -1;
    private orderedIncomingIds: number[] = [];
    private orderedOutgoingIds: number[] = [];
    private incomingPartialCheckAllowed: boolean;
    private outgoingPartialCheckAllowed: boolean;
    private workItemsLoaded: IObservableValue<boolean> = new ObservableValue(false);
    // Max number of area paths for filtering to not overcomplicate WIQL query
    private areaPathsWiqlFilterLimit = 10;
    // Store filters values for easy determining were values changed or not
    private filtersString?: string;
    // Inverted value of feature flag WebAccess.WorkItemTracking.ServerSideFilteringByKeywordDisabled
    private serverSideFilteringByKeywordEnabled: boolean;
    private ignoredForFilters: Set<number> = new Set<number>();

    public readonly filter = new Filter();
    public filterDataSource: IBoardFilterDataSource;
    private swimlaneRulesAutoMoveService: ISwimlaneRulesAutoMoveService | undefined;

    private columnsValidator: IColumnsValidator;
    private swimlanesValidator: ISwimlanesValidator;

    private boardExtensionId: string;
    private signalr: IKanbanBoardSignalRService;
    private workItemModelService: IWorkItemModelService;
    private workItemSavingService: IWorkItemSavingService;
    private workItemNodeService: IWorkItemNodeService;
    private additionalWiqlConditionsEnabled: boolean;
    private hasPermissionToDeleteWorkItem = new ObservableValue<boolean>(true);
    private areaPathList: IListBoxItem<WorkItemClassificationNode>[];

    private columnLayoutsFilteringFixEnabled: boolean;

    public get boardSettingsData(): IReadonlyObservableValue<IBoardSettingsData | undefined> {
        return this.boardSettingsDataObservable;
    }

    public get isFiltering(): IReadonlyObservableValue<boolean> {
        return this.isFilteringObservable;
    }

    public get boardLayout(): IReadonlyObservableValue<BoardLayout | undefined> {
        return this.boardLayoutObservable;
    }

    public get initialFilterState(): FilterState | undefined {
        return this.initialFilters;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.projectId = getTFSPageData(this.pageContext)?.project?.id ?? "";
        this.workItemService = pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
        this.swimlaneRulesAutoMoveService = pageContext.getService<ISwimlaneRulesAutoMoveService>("ISwimlaneRulesAutoMoveService");
        this.signalr = this.pageContext.getService<IKanbanBoardSignalRService>(IKanbanBoardSignalRServiceId);
        this.workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.workItemSavingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        this.workItemNodeService = this.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.getPermissions(contributionService);
        this.backlogConfiguration = contributionService.getData<BacklogConfiguration>(BacklogConfigurationDataProvider);
        this.permissionData = contributionService.getData<IAdvanceBacklogManagementPermission>(BacklogPermissionDataProvider);

        const icons = contributionService.getData<WorkItemTypeColorAndIcon[]>(WorkItemTypeColorIconDataProvider);

        if (icons) {
            this.workItemTypeColorAndIcons = icons;
        }

        const boardData = contributionService.getData<BoardData>(BoardsDataProvider);
        this.loadBoardData(boardData);
        this.workItemService.subscribe(this.workItemServiceListener);
        this.serverSideFilteringByKeywordEnabled = !isFeatureFlagEnabled(this.pageContext, FeatureFlags.ServerSideFilteringByKeywordDisabled, false);
        this.additionalWiqlConditionsEnabled = !isFeatureFlagEnabled(this.pageContext, FeatureFlags.AdditionalWiqlConditionsDisabled);
        this.columnLayoutsFilteringFixEnabled = !isFeatureFlagEnabled(this.pageContext, FeatureFlags.ColumnLayoutsFilteringFixDisabled);
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this.disposeFilterManager();
        this.workItemService.unsubscribe(this.workItemServiceListener);
        this.signalr.endWatch(this.boardExtensionId);
    }

    public canReorderInColumn(columnId?: string, itemId?: number, doneColumn?: boolean): boolean {
        const columnLayout = this.getColumnLayout(columnId);
        const preserveBacklogOrder = this.boardSettingsData.value?.boardData.boardModel.boardSettings.preserveBacklogOrder;
        const hasPermission = this.permissionData?.hasPermission ?? false;

        let itemCanReorderInColumn = true;
        if (itemId && columnLayout) {
            const item = this.workItemService.get(itemId)?.value;
            if (item) {
                const { columnValue, doneValue } = this.getWorkItemLayoutFieldValues(item);

                // items can always reorder in their current column
                itemCanReorderInColumn = columnValue === columnLayout.name && doneValue === doneColumn;
            }
        }

        return !isOutgoing(columnLayout) && hasPermission && (!preserveBacklogOrder || itemCanReorderInColumn);
    }

    public hasPermissionToDeleteWorkItems(): ObservableValue<boolean> {
        return this.hasPermissionToDeleteWorkItem;
    }

    public getRowsLayout(columnNameOrId?: string): BoardRowLayout[] | undefined {
        const boardLayout = this.boardLayoutObservable.value;
        if (!boardLayout) {
            return;
        }
        if (columnNameOrId) {
            return boardLayout.rows.filter(row => {
                return row.columns.find(column => column.id == columnNameOrId || column.name == columnNameOrId);
            });
        }
        return boardLayout.rows;
    }

    public getRowLayout(rowNameOrId: string): BoardRowLayout | undefined {
        const boardLayout = this.boardLayoutObservable.value;
        if (!boardLayout) {
            return;
        }
        return boardLayout.rows?.find(row => row.id === rowNameOrId || rowNameOrId === row.name);
    }

    public expandOrCollapseAllSwimlanes(expand: boolean): void {
        if (!this.boardLayout.value) {
            return;
        }

        for (const swimlane of this.boardLayout.value.rows) {
            if (swimlane.expanded.value !== expand) {
                swimlane.expanded.value = expand;
            }
        }
    }

    public getColumnsLayout(): BoardColumnLayout[] | undefined {
        const boardLayout = this.boardLayoutObservable.value;
        if (!boardLayout) {
            return;
        }
        return boardLayout.columns;
    }

    public getColumnLayout(columnNameOrId?: string, rowNameOrId?: string | null, isDone = false): BoardColumnLayout | undefined {
        const boardLayout = this.boardLayoutObservable.value;
        if (!boardLayout || !columnNameOrId) {
            return;
        }

        const { columns, rows } = boardLayout;
        let columnSet: BoardColumnLayout[] | undefined = columns;
        const column = this.columnLayoutsFilteringFixEnabled
            ? columns.find(column => column.id === columnNameOrId || column.name === columnNameOrId)
            : undefined;
        if (column && column.columnType !== BoardColumnType.InProgress) {
            // outgoing and incoming columns are not split by swimlane
            // and can not be splitted into 'doing/done'
            return column;
        } else if (rowNameOrId !== undefined) {
            columnSet = rows.find(row => row.id === rowNameOrId || rowNameOrId === row.name)?.columns;
        } else if (isDone) {
            // When we have only one default swimlane, rowNameOrId will be undefined, so need to handle this for splitted Done column
            const splittedDoneColumnSet = rows.find(r =>
                r.columns.some(c => (c.id === columnNameOrId || c.name === columnNameOrId) && !!c.isDone && c.isSplit)
            )?.columns;
            columnSet = splittedDoneColumnSet ?? columnSet;
        }

        return columnSet?.find(
            column => (column.id === columnNameOrId || column.name === columnNameOrId) && (!column.isSplit || isDone === !!column.isDone)
        );
    }

    public getWorkItemsLoaded(): IObservableValue<boolean> {
        return this.workItemsLoaded;
    }

    public async loadNextPartialWorkItems(column: BoardColumnLayout, textFilter?: string): Promise<void> {
        if (isInProgress(column) || !this.boardLayout.value || !this.boardSettingsData.value) {
            return;
        }

        const filterState = this.getFilterStateForWIQL();
        textFilter = textFilter ?? this.getTextFilter();

        const boardData = this.boardSettingsData.value.boardData;
        const extensionId = boardData.boardModel.boardSettings.extensionId;

        await this.updateIdsMatchingFilter(column, this.projectId, filterState, textFilter, boardData);

        const pageSize = this.isFilteringColumn(column) ? boardData.boardModel.board.filterPageSize : boardData.boardModel.board.pageSize;
        const idsToPage = this.getNextPageIds(column.columnType, pageSize);

        if (idsToPage.length > 0) {
            const workItemSource = this.getWorkItemSource(boardData);
            const fieldDefinitions = getFieldEntriesFromBoardFields(workItemSource.fieldDefinitions);
            const pagedWorkItems = await this.workItemService.loadWorkItems(this.projectId, idsToPage, fieldDefinitions, extensionId);
            if (!pagedWorkItems) {
                return;
            }

            const observableItems = this.loadBoardWorkItems(pagedWorkItems, boardData);

            await this.populateWorkItemParents(observableItems, boardData);

            this.updateFilteredItems();
            const orderFieldRefName = this.boardSettingsData.value?.orderField?.referenceName;
            column.workItems.value = [...column.workItems.value, ...observableItems].sort(getObservableWorkItemSorter(orderFieldRefName));
            column.hasPartialItems!.value = this.hasPartialItems(column);
        } else {
            column.hasPartialItems!.value = false;
        }
    }

    public async reloadBoardData(): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.boardLayoutObservable.value = undefined;
        this.boardSettingsDataObservable.value = undefined;
        const messageService = getBoardsMessagesService(this.pageContext);
        messageService.dismissMessage();
        this.workItemService.clearWorkItemModels();
        this.workItemsLoaded.value = false;
        this.signalr.endWatch(this.boardExtensionId);
        this.disposeFilterManager();
        const boardData = await contributionService.getDataAsync<BoardData>(BoardsDataProvider, undefined, /** ignoreCache */ true);
        await this.loadBoardData(boardData);
    }

    public async reloadAnnotaionData(itemIds: number[], teamId: string): Promise<void> {
        if (this.boardSettingsData.value?.annotationSettings) {
            const annotationService = this.pageContext.getService<IAnnotationService>("IAnnotationService");
            await annotationService.getAnnotationsForItemsAsync(
                itemIds,
                teamId,
                this.boardSettingsData.value?.annotationSettings,
                this.boardSettingsData.value?.orderField?.referenceName
            );
        }
    }

    public async moveItem(itemId: number, columnId: string, rowId?: string, doneColumn = false, previousId?: number, nextId?: number) {
        const teamId = this.boardSettingsData.value?.boardData.backlogContextData.team.id ?? "";
        const orderFieldRefName = this.boardSettingsData.value?.orderField?.referenceName;
        if (!this.canReorderInColumn(columnId)) {
            previousId = undefined;
            nextId = undefined;
        }

        const observableItem = this.workItemService.get(itemId);
        if (!observableItem) {
            console.error(`Could not find observable item ${itemId}`);
            return;
        }

        const { columnField, doneField, rowField } = this.boardFields!;
        const startColumnName = observableItem.value.getFieldValue<string>(this.boardFields!.columnField);
        const startColumn = this.boardLayout.value?.columns.find(c => c.name === startColumnName);
        const targetColumn = this.boardLayout.value?.columns.find(c => c.id === columnId);
        if (!targetColumn || !startColumn) {
            console.error(`Could not find column ${!targetColumn ? columnId : startColumnName}`);
            return;
        }

        const startRowName = observableItem.value.getFieldValue<string | null>(this.boardFields!.rowField) ?? null;
        const startRow = this.boardLayout.value?.rows.find(r => r.name === startRowName);

        if (this.swimlaneRulesAutoMoveService && isInProgress(targetColumn)) {
            rowId = await this.swimlaneRulesAutoMoveService.calculateAppropriateSwimlane(observableItem.value, rowId);
        }
        let targetRow: BoardRowLayout | undefined;
        if (rowId) {
            targetRow = this.boardLayout.value?.rows.find(r => r.id === rowId);
        } else if (isInProgress(targetColumn)) {
            // use the default row if no row was specified for an in progress column
            targetRow = this.boardLayout.value?.rows.find(r => r.isDefault);
        }

        const columnChanged = startColumnName !== targetColumn.name;
        const rowChanged = startRowName !== targetRow?.name;
        const currentDoneValue = !!observableItem.value.getFieldValue<boolean>(doneField);
        const doneChanged = currentDoneValue !== doneColumn;

        const reorderRequired = previousId || nextId;
        const columnMoveRequired = columnChanged || rowChanged || doneChanged;

        try {
            const itemType = observableItem.value.getRequiredFieldValue<string>(CoreFieldRefNames.WorkItemType);
            const targetState = targetColumn.stateMappings[itemType];
            if (!targetState) {
                console.error(`Could not find target state for work item type ${itemType} in column ${columnId}`);
                return;
            }

            if (reorderRequired || columnMoveRequired) {
                observableItem.saving();
                this.updateLayoutForWorkItemMove(
                    observableItem,
                    startColumn,
                    targetColumn,
                    startRow,
                    targetRow,
                    previousId,
                    nextId,
                    currentDoneValue,
                    doneColumn
                );
            }

            if (columnMoveRequired) {
                if (columnChanged) {
                    observableItem.value.setFieldValue(columnField, targetColumn.name);
                    observableItem.value.setFieldValue(CoreFieldRefNames.State, targetState);
                }
                if (rowChanged && !isOutgoing(targetColumn)) {
                    observableItem.value.setFieldValue(rowField, targetRow?.name ?? null);
                }
                if (doneChanged) {
                    observableItem.value.setFieldValue(doneField, doneColumn);
                }

                if (await observableItem.value.isValid(this.pageContext)) {
                    await observableItem.value.save(this.pageContext).then(async () => {
                        // callback on successful save.
                        // If this drop is the result of changing between the 'done'
                        // split of two different columns we will need two saves because
                        // the first save will overwrite the done state to false.
                        if (currentDoneValue && columnChanged && !doneChanged) {
                            observableItem.value.setFieldValue(doneField, doneColumn);
                            // Trigger a second save to set the item state to 'done'
                            // now that it has been reset to the doing state of the
                            // correct column.
                            await observableItem.value.save(this.pageContext);
                        }
                    });
                }
            }

            if (reorderRequired) {
                this.workItemService.reorderWorkItems(
                    itemId,
                    this.projectId,
                    teamId,
                    previousId,
                    nextId,
                    undefined,
                    {
                        onFailure: this.onReorderFailure,
                        saveToServer: true,
                        saveToServerEvenIfAlreadySorted: true
                    },
                    orderFieldRefName
                );
            }
            observableItem.updateBackgroundColor(this.pageContext, this.cardStyles);
        } catch (err) {
            // TODO: error handling
            console.error("Error: %o", err);
            // reset after failed move
            this.updateLayoutForWorkItemMove(
                observableItem,
                targetColumn,
                startColumn,
                targetRow,
                startRow,
                undefined,
                undefined,
                doneColumn,
                currentDoneValue
            );
        } finally {
            observableItem.saveComplete();
            this.updateFilteredItems();
        }
    }

    private async updateItemCommon(
        observableItem: IObservableBoardsWorkItem,
        changeMethod: (item: IObservableBoardsWorkItem) => void,
        saveMethod: (item: IObservableBoardsWorkItem) => Promise<any>
    ) {
        try {
            observableItem.value.isTitleEditing.value = false;
            observableItem.value.saving = true;
            changeMethod(observableItem);

            await this.swimlaneRulesAutoMoveService?.tryAutoMove(observableItem.value);

            if (await observableItem.value.isValid(this.pageContext)) {
                await saveMethod(observableItem);

                this.ignoredForFilters.add(observableItem.value.id);
            }
            observableItem.updateBackgroundColor(this.pageContext, this.cardStyles);
        } catch (err) {
            getBoardsMessagesService(this.pageContext).showMessage({
                renderMessage: () => <span>{err?.message ?? err}</span>,
                severity: MessageCardSeverity.Error
            });
            console.error("Error: %O", err);
        } finally {
            observableItem.saveComplete();
            this.updateBoardLayout();
        }
    }

    public async updateItem(itemId: number, fieldReferenceName: string, newValue: any) {
        const observableItem = this.workItemService.get(itemId);
        if (!observableItem) {
            console.error(`Could not find observable item ${itemId}`);
            return;
        }

        this.updateItemCommon(
            observableItem,
            item => {
                item.value.setFieldValue(fieldReferenceName, newValue);
            },
            item => {
                return item.value.save(this.pageContext);
            }
        );
    }

    public async updateItemIteration(selectedWorkItem: IObservableBoardsWorkItem, iteration: IIterationData): Promise<void> {
        const checklist = selectedWorkItem.value.annotations?.checklist;
        const workItemIds: number[] = [selectedWorkItem.value.id];
        if (checklist) {
            Object.keys(checklist).forEach(key => {
                const childItems = checklist[key].items;
                for (const childItem of childItems.value) {
                    if (childItem.completed) {
                        continue;
                    }
                    workItemIds.push(childItem.id);
                }
            });
        }

        const workItems = await this.workItemModelService.getWorkItems(workItemIds);

        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings();
        const isRootIteration = teamSettings && teamSettings.backlogIteration.id === iteration.id;

        this.updateItemCommon(
            selectedWorkItem,
            item => {
                workItems.map(workItem => {
                    !isRootIteration && workItem.setFieldValueByName(CoreFieldRefNames.IterationId, iteration.nodeId);
                    workItem.setFieldValueByName(CoreFieldRefNames.IterationPath, iteration.friendlyPath);
                });
            },
            item => {
                return this.workItemSavingService.saveWorkItemsBatchAsync(workItems);
            }
        );
    }
    
    public async unwatchSignalR(): Promise<void> {
        await this.signalr.endWatch(this.boardExtensionId);
    }

    private async takeWorkItemsSignalRUpdate(updatePayload: IBoardUpdatePayload[]): Promise<void> {
        const updatePromises = updatePayload.map(async update => {
            switch (update.changeType) {
                case WorkItemSignalREventType.ItemAdded:
                case WorkItemSignalREventType.ItemCreated:
                case WorkItemSignalREventType.ItemRestored:
                    return this.addItemFromSignalR(update.id);
                case WorkItemSignalREventType.ItemUpdated:
                    return this.updateItemFromSignalR(update.id, update);
                case WorkItemSignalREventType.ItemDeleted:
                case WorkItemSignalREventType.ItemRemoved:
                    return this.removeItem(update.id);
                default:
                    break;
            }
        });

        const anyUpdated = (await Promise.all(updatePromises)).some(updated => updated);

        if (anyUpdated) {
            this.updateBoardLayout();
        }
    }

    public async addItemFromSignalR(itemId: number): Promise<boolean> {
        const boardData = this.boardSettingsData.value?.boardData;
        if (!boardData) {
            return false;
        }
        const workItemData = this.getWorkItemSource(boardData);
        const extensionId = boardData.boardModel.boardSettings.extensionId;
        const fieldDefinitions = getFieldEntriesFromBoardFields(workItemData.fieldDefinitions);

        // Load only required work items
        const newWorkItems = await this.workItemService.loadWorkItems(this.projectId, [itemId], fieldDefinitions, extensionId);
        if (!newWorkItems) {
            return false;
        }
        const observableItems = this.loadBoardWorkItems(newWorkItems, boardData);
        this.populateWorkItemParents(observableItems, boardData);

        this.updateFilteredItems();
        return true;
    }

    public async updateItemFromSignalR(itemId: number, updatePayload: IBoardUpdatePayload): Promise<boolean> {
        const observableItem = this.workItemService.get(itemId);

        // "Add item" case occurs when the signalR update event fires, but still there is no workItem with itemId
        let updated = observableItem ? false : await this.addItemFromSignalR(itemId);

        // if the revisions match, the update was initiated from the board and we should ignore it
        const clientRevision = observableItem?.value.getFieldValue<number>(CoreFieldRefNames.Rev);
        if (clientRevision === updatePayload.revision) {
            return false;
        }

        // Updated flag should be true when item was successfully added or refreshed
        updated = updated || ((await observableItem?.value.refresh(this.pageContext, { doNotResetIfManuallyDirty: true })) ?? false);
        if (updated) {
            observableItem?.notify();
        }
        return updated;
    }

    public removeItem(itemId: number): boolean {
        const workItem = this.workItemService.get(itemId);
        if (!this.boardLayoutObservable.value || !this.boardFields || !workItem) {
            return false;
        }

        const { columnValue, rowValue, doneValue } = this.getWorkItemLayoutFieldValues(workItem.value);
        if (!columnValue) {
            this.boardLayout.value?.columns.forEach(column => column.workItems.removeAll(item => item.value.id === itemId));
            this.boardLayout.value?.rows.forEach(row => row.columns.forEach(column => column.workItems.removeAll(item => item.value.id === itemId)));
        } else {
            const fullColumnLayout = this.getColumnLayout(columnValue);
            const partialColumnLayout = this.getColumnLayout(columnValue, rowValue ?? null, doneValue);
            fullColumnLayout?.workItems.removeAll(item => item.value.id === itemId);
            partialColumnLayout?.workItems.removeAll(item => item.value.id === itemId);
        }

        this.workItemService.clear(itemId);
        return true;
    }

    public getRowNameError(rowId: string): IReadonlyObservableValue<string | undefined> {
        return this.swimlanesValidator.getSwimlaneNameError(rowId);
    }

    public async renameRow(rowId: string, newRowName: string): Promise<void> {
        const boardSettings = this.boardSettingsDataObservable.value?.boardData.boardModel.boardSettings;
        if (!boardSettings) {
            return;
        }

        const previousRows = boardSettings.rows;
        const updatedRows: BoardRow[] = [];

        let edited = false;
        for (const row of previousRows) {
            if (row.id === rowId && row.name !== newRowName) {
                edited = true;
                updatedRows.push({ ...row, name: newRowName });
            } else {
                updatedRows.push(row);
            }
        }

        if (!edited) {
            return;
        }

        // Update the observables
        this.setRows(updatedRows);

        this.swimlanesValidator.validate(updatedRows);
        if (!this.swimlanesValidator.isValid.value) {
            return;
        }

        try {
            // Update the rows on the server.
            // Do not do a full refresh because we know exactly what data ought to have changed.
            const teamContext = {
                teamId: boardSettings.teamId,
                projectId: getTFSPageData(this.pageContext)?.project?.id ?? ""
            } as TeamContext;
            const workRestClient = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
            await workRestClient.updateBoardRows(updatedRows, teamContext, boardSettings.id);
        } catch (e: unknown) {
            // The update failed - restore the original observables
            this.setRows(previousRows);
        }
    }

    /**
     * Return the row (swimlane) that displays the work item.
     */
    public getWorkItemRowLayout(itemId: number): BoardRowLayout | undefined {
        const workItem = this.workItemService.get(itemId);
        if (!workItem) {
            return;
        }
        const rowValue = this.getWorkItemLayoutFieldValues(workItem.value).rowValue;
        if (rowValue) {
            return this.getRowLayout(rowValue);
        }
    }

    private setRows(rows: BoardRow[]) {
        if (!this.boardSettingsDataObservable.value || !this.boardLayoutObservable.value) {
            return;
        }

        // Update the row settings deep inside the settings observable
        this.boardSettingsDataObservable.value = {
            ...this.boardSettingsDataObservable.value,
            boardData: {
                ...this.boardSettingsDataObservable.value.boardData,
                boardModel: {
                    ...this.boardSettingsDataObservable.value.boardData.boardModel,
                    boardSettings: {
                        ...this.boardSettingsDataObservable.value.boardData.boardModel.boardSettings,
                        rows
                    }
                }
            }
        };

        // Merge the row settings into the row layouts
        this.boardLayoutObservable.value = {
            ...this.boardLayoutObservable.value,
            rows: this.boardLayoutObservable.value.rows.map((row, i) => ({ ...row, ...rows[i] }))
        };
    }

    public getColumnNameError(columnId: string): IReadonlyObservableValue<string | undefined> {
        return this.columnsValidator.getNameError(columnId);
    }

    public async renameColumn(columnId: string, newColumnName: string): Promise<void> {
        const boardSettings = this.boardSettingsDataObservable.value?.boardData.boardModel.boardSettings;
        if (!boardSettings) {
            return;
        }

        const previousColumns = boardSettings.columns;
        const updatedColumns: BoardColumn[] = [];

        let edited = false;
        for (const column of previousColumns) {
            if (column.id === columnId && column.name !== newColumnName) {
                edited = true;
                updatedColumns.push({ ...column, name: newColumnName });
            } else {
                updatedColumns.push(column);
            }
        }

        if (!edited) {
            return;
        }

        // Update the observables
        this.setColumns(updatedColumns);

        this.columnsValidator.validate(updatedColumns);
        if (!this.columnsValidator.isValid.value) {
            return;
        }

        try {
            // Update the columns on the server.
            // Do not do a full refresh because we know exactly what data ought to have changed.
            const teamContext = {
                teamId: boardSettings.teamId,
                projectId: getTFSPageData(this.pageContext)?.project?.id ?? ""
            } as TeamContext;
            const workRestClient = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
            await workRestClient.updateBoardColumns(updatedColumns as unknown as WorkContracts.BoardColumn[], teamContext, boardSettings.id);
        } catch (e: unknown) {
            // The update failed - restore the original observables
            this.setColumns(previousColumns);
        }
    }

    /**
     * Return the column that displays the work item.
     */
    public getWorkItemColumnLayout(itemId: number): BoardColumnLayout | undefined {
        const workItem = this.workItemService.get(itemId);
        if (!workItem) {
            return;
        }

        const { columnValue, doneValue, rowValue } = this.getWorkItemLayoutFieldValues(workItem.value);
        if (rowValue) {
            return this.getColumnLayout(columnValue, rowValue, doneValue);
        } else {
            // need to handle the case where a fully loaded work item is in the default row (rowValue is null)
            // server by default doesn't include null field values in payload so it looks like its undefined when
            // it is really null

            // try to get the partial column layout in the default row
            let columnLayout = this.getColumnLayout(columnValue, null, doneValue);
            if (!columnLayout) {
                // if it's not in the default row, must be in incoming or outgoing column and we don't need to worry
                // about row or done values
                columnLayout = this.getColumnLayout(columnValue);
            }

            return columnLayout;
        }
    }

    public getBacklogDefaultWorkItemTypeName = async (): Promise<string | undefined> => {
        const allBacklogLevels: WorkContracts.BacklogLevelConfiguration[] = this.backlogConfiguration?.portfolioBacklogs
            ? [...this.backlogConfiguration?.portfolioBacklogs]
            : [];
        if (this.backlogConfiguration?.requirementBacklog) {
            allBacklogLevels.push(this.backlogConfiguration?.requirementBacklog);
        }
        if (this.backlogConfiguration?.taskBacklog) {
            allBacklogLevels.push(this.backlogConfiguration?.taskBacklog);
        }
        const currentBacklogLevelId = this.boardLayout.value?.backlogLevelId;
        const currentBacklogLevel = allBacklogLevels.find(
            backlogLevel => backlogLevel.id === currentBacklogLevelId || backlogLevel.name === currentBacklogLevelId
        );
        return currentBacklogLevel?.defaultWorkItemType as unknown as string;
    };

    public refreshBoardLayout(): void {
        this.updateBoardLayout();
    }

    private setColumns(columns: BoardColumn[]) {
        if (!this.boardSettingsDataObservable.value || !this.boardLayoutObservable.value) {
            return;
        }

        // Update the column settings deep inside the settings observable
        this.boardSettingsDataObservable.value = {
            ...this.boardSettingsDataObservable.value,
            boardData: {
                ...this.boardSettingsDataObservable.value.boardData,
                boardModel: {
                    ...this.boardSettingsDataObservable.value.boardData.boardModel,
                    boardSettings: {
                        ...this.boardSettingsDataObservable.value.boardData.boardModel.boardSettings,
                        columns
                    }
                }
            }
        };

        // Merge the column settings into the column layout
        this.boardLayoutObservable.value = {
            ...this.boardLayoutObservable.value,
            columns: this.boardLayoutObservable.value.columns.map((column, i) => ({ ...column, ...columns[i] }))
        };
    }

    private getWorkItemsForBoardSegment(
        workItems: IObservableBoardsWorkItem[],
        column: BoardColumn,
        row?: BoardRow,
        isDone?: boolean,
        allRowNames?: string[]
    ): IObservableBoardsWorkItem[] {
        // TODO: maybe optimize this with better data structuring
        if (!this.boardFields) {
            return [];
        }

        return workItems.filter(workItem => this.workItemMatchesBoardSegment(workItem, column, row, isDone, allRowNames));
    }

    private workItemMatchesBoardSegment(
        workItem: IObservableBoardsWorkItem,
        column: BoardColumn,
        row?: BoardRow,
        isDone?: boolean,
        allRowNames: string[] = []
    ): boolean {
        if (!this.boardFields) {
            return false;
        }

        const { columnField, doneField, rowField } = this.boardFields;
        const itemColumnName = workItem.value.getFieldValue<string>(columnField);
        const itemRowName = workItem.value.getFieldValue<string>(rowField);
        const isItemDone = !!workItem.value.getFieldValue<boolean>(doneField);

        // null row name indicates the default row for in progress columns even if the default row has a name
        const matchNullRowNames = row?.isDefault && isInProgress(column);

        if (itemColumnName !== column.name) {
            // Wrong column
            return false;
        }

        if (row === undefined) {
            // This is an incoming/outgoing column; the row and done status do not matter
            // note: intentionally comparing rowName to undefined because null is a valid rowName value
            return true;
        }

        if (isDone !== undefined && isItemDone !== isDone) {
            // Wrong section of a split column
            return false;
        }

        if (matchNullRowNames) {
            // This is the default row in an in-progress column
            if (itemRowName === row.name) {
                // Exact match
                return true;
            }

            // If the work item's row does not match any of the other rows, it should go in this one.
            return !itemRowName || !allRowNames.includes(itemRowName);
        } else {
            // This is a normal row. Match on the exact row name.
            return itemRowName === row.name;
        }
    }

    private getWorkItemTypeIcons() {
        return this.workItemTypeColorAndIcons.sort((a, b) => localeIgnoreCaseComparer(a.workItemTypeName, b.workItemTypeName));
    }

    private getBacklogLevelWorkItemTypeIcons(boardData: BoardData) {
        const workItemTypeColorAndIcons = this.getWorkItemTypeIcons();
        const workItemDataSource = this.getWorkItemSource(boardData);
        const sortedBoardWorkItemTypes = workItemDataSource.itemTypes;
        return workItemTypeColorAndIcons
            .filter(typeData => sortedBoardWorkItemTypes.includes(typeData.workItemTypeName))
            .sort((a, b) => sortedBoardWorkItemTypes.indexOf(a.workItemTypeName) - sortedBoardWorkItemTypes.indexOf(b.workItemTypeName));
    }

    private convertEnabledWorkItemTypesToSet(boardData: BoardData): Set<string> {
        if (!boardData || !boardData.enabledWorkItemTypes) {
            return new Set<string>();
        }

        const enabledWorkItemTypes = new Set<string>(boardData.enabledWorkItemTypes);

        return enabledWorkItemTypes;
    }

    private async loadBoardData(boardData?: BoardData): Promise<void> {
        if (!boardData || boardData?.exceptionInfo) {
            return;
        }

        // each board has a unique work item field value for row and column
        const boardFields = boardData.boardModel.boardSettings.boardFields;
        this.boardFields = {
            columnField: boardFields[BoardFieldNameKeys.ColumnField] ?? "",
            doneField: boardFields[BoardFieldNameKeys.DoneField] ?? "",
            rowField: boardFields[BoardFieldNameKeys.RowField] ?? ""
        };

        const { annotations } = divyCardRules(boardData.boardModel.boardCardSettings.styles);
        const childBacklogConfiguration = this.getChildBacklogConfiguration(boardData.backlogContextData.levelName, this.backlogConfiguration);
        const annotationSettings = childBacklogConfiguration
            ? populateAnnotationSettings(annotations, boardData.isTestPlansEnabled && boardData.hasTestPlansLicense, childBacklogConfiguration)
            : [];

        const isBoardValid = boardData.boardModel.boardSettings.isBoardValid;
        if (!isBoardValid) {
            const personIdentities = await this.getPersonIdentities();
            this.boardSettingsDataObservable.value = { annotationSettings, boardData, boardFields: this.boardFields, personIdentities };
            const messageService = getBoardsMessagesService(this.pageContext);
            messageService.showMessage({
                renderMessage: () => (
                    <InvalidBoardMessage
                        settingsPanelProps={{
                            boardSettingsData: this.boardSettingsDataObservable,
                            onSave: async () => {
                                this.reloadBoardData();
                                return true;
                            },
                            teamId: boardData.backlogContextData.team.id
                        }}
                    />
                ),
                severity: MessageCardSeverity.Error
            });
            return;
        }

        this.filterDataSource = new BoardFilterDataSource(this.workItemService, getFilterableFields(boardData), this.pageContext);
        await this.initializeFilterManagers(this.filterDataSource);
        await this.initializeExpandableSettings(boardData.boardModel.boardSettings, boardData.backlogContextData.team.id);

        this.columnsValidator = new ColumnsValidator(boardData.boardModel.boardSettings.allowedMappings);
        this.swimlanesValidator = new SwimlanesValidator();

        this.initialFilters = boardData.initialFilters;
        this.boardExtensionId = boardData.boardModel.boardSettings.extensionId;

        const workItemSource = this.getWorkItemSource(boardData);
        const fieldDefinitions = getFieldEntriesFromBoardFields(workItemSource.fieldDefinitions);

        const effortFieldName = boardData.boardModel.board.fields["Effort"];
        const orderFieldName = boardData.boardModel.board.fields["Order"];
        const effortField = fieldDefinitions.find(field => field.referenceName === effortFieldName);
        const orderField = fieldDefinitions.find(field => field.referenceName === orderFieldName);

        const personIdentities = await this.getPersonIdentities();
        try {
            //force the field service to load all fields in order to use them later inside Card
            if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.CardsUseNewWorkItemFieldService, false)) {
                const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
                await fieldService.loadFields(); 
            } else {
                const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
                await fieldService.getFieldsAsync();
            }
        } catch (error) {
            console.error("An error occurred while loading fields:", error);
        }
        this.boardSettingsDataObservable.value = {
            annotationSettings,
            boardData,
            boardFields: this.boardFields,
            effortField,
            orderField,
            personIdentities
        };

        this.orderedIncomingIds = workItemSource.payload.orderedIncomingIds;
        this.orderedOutgoingIds = workItemSource.payload.orderedOutgoingIds;
        this.nextIncomingIndexToPage = this.orderedIncomingIds.length > 0 ? 0 : -1;
        this.nextOutgoingIndexToPage = this.orderedOutgoingIds.length > 0 ? 0 : -1;
        this.incomingPartialCheckAllowed = true;
        this.outgoingPartialCheckAllowed = true;

        this.loadWorkItemData(boardData, fieldDefinitions);
        this.updateBoardLayout();
        if (this.swimlaneRulesAutoMoveService) {
            await this.swimlaneRulesAutoMoveService?.initialAutoMove(this.workItemService.getAllItems(), boardData);
            this.updateBoardLayout();
        }
        this.signalr.watch(this.boardExtensionId, this.takeWorkItemsSignalRUpdate.bind(this), this.reloadBoardData.bind(this));
    }

    private async getPersonIdentities(): Promise<IIdentity[] | undefined> {
        const filterIdentities = (entityList: IIdentity[]) => entityList && entityList.filter(entity => !!entity.localId);
        const peopleProvider = new PeoplePickerProvider(this.pageContext, undefined, filterIdentities);
        return peopleProvider.onEmptyInputFocus(/*maxItemsCount*/ 10);
    }

    private loadWorkItemData(boardData: BoardData, fieldDefinitions: IFieldEntry[]): void {
        this.workItemService.clearAll();
        const workItemData = this.getWorkItemSource(boardData);
        const fieldReferenceNames = workItemData.payload.columns;
        const workItemValues = workItemData.payload.rows;
        const extensionId = boardData.boardModel.boardSettings.extensionId;
        const boardWorkItems = workItemValues.map(fieldValues =>
            BoardsWorkItem.fromValues(fieldValues, fieldReferenceNames, fieldDefinitions, extensionId)
        );
        const observableItems = this.loadBoardWorkItems(boardWorkItems, boardData);
        this.populateWorkItemParents(observableItems, boardData);
    }

    private loadBoardWorkItems(workItems: IBoardsWorkItem[], boardData: BoardData): IObservableBoardsWorkItem[] {
        const projectName = getTFSPageData(this.pageContext)!.project!.name;
        const { cardStyles } = divyCardRules(boardData.boardModel.boardCardSettings.styles);
        this.cardStyles = getCardStylesFromCardRules(cardStyles);
        const observableBoardsItems = [];
        const allIds: number[] = [];
        for (const workItem of workItems) {
            // TODO: do we know that all items on the board come from the project?
            if (!workItem.getFieldValue(CoreFieldRefNames.TeamProject)) {
                workItem.setFieldValue(CoreFieldRefNames.TeamProject, projectName);
            }

            const observableItem = this.workItemService.add(workItem);
            observableItem.updateBackgroundColor(this.pageContext, this.cardStyles);
            observableBoardsItems.push(observableItem);
            allIds.push(workItem.id);
        }

        this.reloadAnnotaionData(allIds, boardData.boardModel.boardSettings.teamId);

        this.filterManager.dataUpdated();
        this.columnFilterManager.dataUpdated();

        return observableBoardsItems;
    }

    private async populateWorkItemParents(observableBoardsItems: IObservableBoardsWorkItem[], boardData: BoardData) {
        const teamId = boardData.backlogContextData.team.id;
        const backlogRefName = boardData.boardModel.boardSettings.backlogLevelId;

        if (this.backlogConfiguration && !isTopBacklogLevel(backlogRefName, this.backlogConfiguration)) {
            const idsToPopulate = observableBoardsItems.map(item => item.value.id);
            await this.workItemService.populateWorkItemParents(teamId, backlogRefName, idsToPopulate);

            const parentFilterItemState = this.filter.getFilterItemState(CoreFieldRefNames.Parent);
            if (parentFilterItemState) {
                // We need to update the current parent filter state due to parents of work items being populated
                // after the main data is loaded and the filter is applied so we need to trigger it again if there is the parent filter
                this.filter.setFilterItemState(CoreFieldRefNames.Parent, parentFilterItemState);
            }

            if (idsToPopulate.length && this.filterManager.isFiltering() && this.isParentFieldShownOnCards(boardData)) {
                this.updateFilteredItems();
            }
        }
    }

    private sortColumn(column: BoardColumnLayout): void {
        const orderFieldRefName = this.boardSettingsData.value?.orderField?.referenceName;
        const sorter = isOutgoing(column) ? observableWorkItemClosedDateSorter : getObservableWorkItemSorter(orderFieldRefName);
        column.workItems.value = column.workItems.value.sort(sorter);
    }

    private updateBoardLayout(): void {
        if (!this.boardSettingsData.value) {
            return;
        }

        const allWorkItems = this.workItemService.getAllItems();
        // Set value once work items are expected in the store
        this.workItemsLoaded.value = true;

        const { boardData } = this.boardSettingsData.value;
        const boardSettings = boardData.boardModel.boardSettings;
        const { tagStyles } = divyCardRules(boardData.boardModel.boardCardSettings.styles);
        const itemSource = boardData.boardModel.itemSource as WorkItemSource;

        const columns: BoardColumnLayout[] = [];
        const rows: BoardRowLayout[] = [];
        let incomingColumn: BoardColumnLayout | undefined = undefined;
        let outgoingColumn: BoardColumnLayout | undefined = undefined;

        for (const column of boardSettings.columns.sort((a, b) => a.order - b.order)) {
            const workItems = new ObservableArray(this.getWorkItemsForBoardSegment(allWorkItems, column));
            const columnLayout: BoardColumnLayout = { ...column, workItems };
            this.sortColumn(columnLayout);
            columns.push(columnLayout);

            if (column.columnType !== BoardColumnType.InProgress) {
                const expanded = this.getExpandedObservable(column.id);
                columnLayout.expanded = expanded;

                const workItemCount = columnLayout.workItems.length;
                if (isIncoming(column)) {
                    columnLayout.columnFilterManager = this.columnFilterManager;
                    // don't show "show more items" button after all work items showed
                    this.incomingPartialCheckAllowed = this.incomingPartialCheckAllowed && this.orderedIncomingIds.length > workItemCount;
                    this.nextIncomingIndexToPage = this.incomingPartialCheckAllowed ? workItemCount : -1;
                    columnLayout.hasPartialItems =
                        this.boardLayoutObservable.value?.incomingColumn.hasPartialItems || new ObservableValue(this.nextIncomingIndexToPage > 0);
                    incomingColumn = columnLayout;
                } else if (isOutgoing(column)) {
                    this.outgoingPartialCheckAllowed = this.outgoingPartialCheckAllowed && this.orderedOutgoingIds.length > workItemCount;
                    this.nextOutgoingIndexToPage = this.outgoingPartialCheckAllowed ? workItemCount : -1;
                    columnLayout.hasPartialItems =
                        this.boardLayoutObservable.value?.outgoingColumn.hasPartialItems || new ObservableValue(this.nextOutgoingIndexToPage > 0);
                    outgoingColumn = columnLayout;
                }
            }
        }

        if (!incomingColumn || !outgoingColumn) {
            $DEBUG && console.warn("Missing incoming or outgoing column.", "Incoming column: ", incomingColumn, "outgoingColumn", outgoingColumn);
            return;
        }

        // swimlanes (rows) only span in-progress columns
        const partialColumns = boardSettings.columns.filter(column => isInProgress(column));
        const allRowNames = boardSettings.rows.map(r => r.name);
        for (const row of boardSettings.rows.sort((a, b) => a.order - b.order)) {
            const rowColumns: BoardColumnLayout[] = [];
            for (const partialColumn of partialColumns) {
                const columnLayout = {
                    ...partialColumn,
                    rowId: row.id,
                    workItems: new ObservableArray(
                        this.getWorkItemsForBoardSegment(
                            allWorkItems,
                            partialColumn,
                            row,
                            /** isDone */ partialColumn.isSplit ? false : undefined,
                            allRowNames
                        )
                    )
                };

                this.sortColumn(columnLayout);
                rowColumns.push(columnLayout);

                if (partialColumn.isSplit) {
                    // add the done side of the split column
                    const columnLayout = {
                        ...partialColumn,
                        isDone: true,
                        rowId: row.id,
                        workItems: new ObservableArray(
                            this.getWorkItemsForBoardSegment(allWorkItems, partialColumn, row, /** isDone */ true, allRowNames)
                        )
                    };

                    this.sortColumn(columnLayout);
                    rowColumns.push(columnLayout);
                }
            }

            const expanded = this.getExpandedObservable(row.id);
            rows.push({ ...row, columns: rowColumns, expanded });
        }

        // map of field referenceNames to FieldLayout
        const fieldLayoutMap = toLookup(itemSource.fieldDefinitions.map(convertBoardFieldDefinitionToFieldLayout), fl => fl.referenceName);
        // map of work item types to CardLayout
        const cardLayout = {} as { [type: string]: CardLayout };
        // the server gives us a map of work item types to the FieldSettings of fields we should display on the cards
        // we need to convert that to a map of work item types to the CardLayouts we should display on the cards
        for (const [workItemType, fields] of Object.entries(boardData.boardModel.boardCardSettings.cards)) {
            let showEmptyFields = false;
            // despite what the generated types say, the contents of fields is an array of string->string dictionaries
            const actualFields: { [key: string]: string | undefined }[] = fields as any;
            const fieldLayouts: FieldLayout[] = [];
            for (const f of actualFields) {
                if (f.fieldIdentifier) {
                    fieldLayouts.push(fieldLayoutMap[f.fieldIdentifier]);
                } else if ("showEmptyFields" in f) {
                    // smh
                    showEmptyFields = f.showEmptyFields?.toLowerCase() === "true";
                }
            }
            cardLayout[workItemType] = {
                ...divyFields(fieldLayouts),
                showEmptyFields
            };
        }

        const icons = this.getWorkItemTypeIcons();

        let boardLayoutSettings: BoardLayout;
        if (this.boardLayoutObservable.value) {
            // reuse setting that won't change unless the board is reloaded
            boardLayoutSettings = { ...this.boardLayoutObservable.value };
        } else {
            boardLayoutSettings = {
                backlogLevelWorkItemTypeColorAndIcons: this.getBacklogLevelWorkItemTypeIcons(boardData),
                backlogLevelId: boardData.boardModel.boardSettings.backlogLevelId,
                cardLayout,
                workItemTypeColorAndIcons: icons,
                workItemTypeColorAndIconsMap: icons.reduce((map, witColorAndIcon) => {
                    map[witColorAndIcon.workItemTypeName] = witColorAndIcon;
                    return map;
                }, {} as WorkItemTypeColorAndIconMap),
                tagStyles: convertCardRulesToTagStyleMap(tagStyles),
                enabledWorkItemTypes: this.convertEnabledWorkItemTypesToSet(boardData)
            } as BoardLayout;
        }

        this.boardLayoutObservable.value = {
            ...boardLayoutSettings,
            columns,
            incomingColumn,
            outgoingColumn,
            rows,
            filteredIds: this.prepareFilteredIds()
        };
    }

    private prepareFilteredIds() {
        if (!this.filterManager.isFiltering()) {
            return;
        }

        const filtered = new Set(this.filterManager.filter(this.pageContext));
        for (const id of this.ignoredForFilters) {
            filtered?.add(id);
        }
        return filtered;
    }

    private getExpandedObservable(id: string) {
        let expanded = this.expandedObservables.get(id);
        if (!expanded) {
            expanded = new ObservableValue(true);
            this.expandedObservables.set(id, expanded);
        }

        return expanded;
    }

    private async initializeExpandableSettings(boardSettings: BoardSettings, teamId: string) {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const result: { [key: string]: any } | undefined = await settingsService.getEntriesAsync(
            BoardSettingsConstants.BoardSettingsPath,
            SettingsUserScope.Me,
            "team",
            teamId
        );
        const parsedResults = new Map<string, boolean>();
        for (const [key, value] of Object.entries(result)) {
            const [id, expandedKey] = key.split("/");
            if (expandedKey === BoardSettingsConstants.ExpandedKey) {
                parsedResults.set(id, value);
            }
        }

        const retrieveSettings = (id: string) => {
            if (!this.expandedObservables.get(id)) {
                const expanded = parsedResults.get(id) ?? true;
                this.expandedObservables.set(id, new ObservableValue(!!expanded));
            }
        };

        for (const col of boardSettings.columns.filter(c => !isInProgress(c))) {
            retrieveSettings(col.id);
        }

        for (const row of boardSettings.rows) {
            retrieveSettings(row.id);
        }
    }

    private updateLayoutForWorkItemMove(
        workItem: IObservableBoardsWorkItem,
        startColumn: BoardColumnLayout,
        targetColumn: BoardColumnLayout,
        startRow?: BoardRowLayout,
        targetRow?: BoardRowLayout,
        previousId?: number,
        nextId?: number,
        wasDone = false,
        isDone = false
    ): void {
        // Remove work item from a column and add it to a new one.
        // This should be done every time WI is moved anywhere in order to keep column up to date.
        startColumn.workItems.removeAll(item => item.value.id === workItem.value.id);
        this.insertWorkItemIntoColumn(targetColumn, workItem, previousId, nextId);

        const partialStartColumn = startRow?.columns.find(column => column.id === startColumn.id && (!column.isSplit || !!column.isDone === wasDone));
        const partialTargetColumn = targetRow?.columns.find(column => column.id === targetColumn.id && !!column.isDone === isDone);

        // Remove work item from previous column (considering row)
        if (partialStartColumn) {
            partialStartColumn.workItems.removeAll(item => item.value.id === workItem.value.id);
        }

        // Add work item to new column (considering row)
        if (partialTargetColumn) {
            this.insertWorkItemIntoColumn(partialTargetColumn, workItem, previousId, nextId);
        }

        // If not expanded swimlane, expand it
        if (targetRow && !targetRow.expanded.value) {
            targetRow.expanded.value = true;
        }
    }

    private getChildBacklogConfiguration(levelName: string, backlogConfiguration?: BacklogConfiguration): BacklogLevelConfiguration | undefined {
        if (!backlogConfiguration) {
            return;
        }

        const backlogLevels = [...backlogConfiguration.portfolioBacklogs, backlogConfiguration.requirementBacklog, backlogConfiguration.taskBacklog];

        backlogLevels.sort((firstBacklogLevel, secondBacklogLevel) => secondBacklogLevel.rank - firstBacklogLevel.rank);
        const currentBacklogLevel = backlogLevels.find(backlog => backlog.name === levelName);
        if (!currentBacklogLevel) {
            return;
        }

        // we are getting BacklogConfiguration from one file, and BacklogLevelConfiguration from another. This is because the BacklogLevelConfiguration
        // model is incorrect, but getting genclient to generate the correct BackogConfiguration is proving rather difficult.
        return backlogLevels.find(backlog => backlog.rank < currentBacklogLevel.rank) as unknown as BacklogLevelConfiguration;
    }

    private disposeFilterManager(): void {
        if (this.filterManager) {
            this.filterManager.clearFilterProviders();
            this.filterManager.deactivate();
            this.filter.unsubscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
        }
    }

    private getWorkItemSource(boardData: BoardData): WorkItemSource {
        return boardData.boardModel.itemSource as WorkItemSource;
    }

    private async initializeFilterManagers(dataSource: IBoardFilterDataSource): Promise<void> {
        this.filterManager = new FilterManager(dataSource);
        this.columnFilterManager = new FilterManager(dataSource);

        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings({ ignoreCache: true });

        this.filter.subscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
        this.filterManager.activate(this.pageContext);
        this.filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new SubstringTextFilterProvider());
        this.filterManager.registerFilterProvider(CoreFieldRefNames.WorkItemType, new FieldFilterProvider(CoreFieldRefNames.WorkItemType));
        this.filterManager.registerFilterProvider(CoreFieldRefNames.State, new FieldFilterProvider(CoreFieldRefNames.State));
        this.filterManager.registerFilterProvider(CoreFieldRefNames.AreaPath, new AreaPathFilterProvider());
        this.filterManager.registerFilterProvider(
            CoreFieldRefNames.IterationPath,
            new IterationPathFilterProvider(teamSettings?.currentIteration?.friendlyPath)
        );
        this.filterManager.registerFilterProvider(CoreFieldRefNames.Tags, new TagsFilterProvider());
        this.filterManager.registerFilterProvider(CoreFieldRefNames.Parent, new ParentFilterProvider());
        pageData && this.filterManager.registerFilterProvider(CoreFieldRefNames.AssignedTo, new AssignedToFilterProvider(pageData.user, this.pageContext));

        this.columnFilterManager.activate(this.pageContext);
        this.columnFilterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new SubstringTextFilterProvider());
    }

    /**
     * Return the current value of the board's text filter
     */
    private getTextFilter(): string | undefined {
        const textFilter = this.filterManager.getFilterProvider(TextFilterProvider.PROVIDER_TYPE);
        if (!textFilter.isFiltering()) {
            return;
        }

        const filterValues = textFilter.getFilter().values;
        return filterValues.length ? (filterValues[0] as string) : undefined;
    }

    /**
     * Checks if the board filter or the column's filter are active
     */
    private isFilteringColumn(column?: BoardColumnLayout): boolean {
        return this.filterManager.isFiltering() || !!column?.columnFilterManager?.isFiltering();
    }

    private updateFilteredItems(): void {
        if (this.boardLayoutObservable.value) {
            this.boardLayoutObservable.value = {
                ...this.boardLayoutObservable.value,
                filteredIds: this.prepareFilteredIds()
            };
        }
    }

    /**
     * Updates the state to contain a new set of IDs matching the current filter
     * @param projectId The project for which to fetch matching work item IDs
     * @param textFilter The keyword text filter to use
     * @param filterState Contains all filters, except TextFilterProvider.PROVIDER_TYPE provider
     */
    private async updateIdsMatchingFilter(
        column: BoardColumnLayout,
        projectId: string,
        filterState: FilterState,
        textFilter?: string,
        boardData?: BoardData
    ): Promise<void> {
        const isFilterStateEmpty = Object.keys(filterState).length === 0;
        if (!textFilter && isFilterStateEmpty) {
            return;
        }

        const isIncoming = column.columnType === BoardColumnType.Incoming;
        if (textFilter) {
            textFilter = sanitizeFilter(textFilter);
        }
        const filtersStr = this.getFiltersString(filterState, textFilter);
        if (filtersStr === (isIncoming ? this.lastIncomingFiltersStr : this.lastOutgoingFiltersStr)) {
            // The filter has not changed, so we can continue to use the same list of matching IDs
            return;
        }

        let matchingIds = [];
        if (textFilter && this.isParentFieldShownOnCards(boardData)) {
            // To be able to filter by parents' names work items should be fetched and their parents should be populated.
            // Because of that we just use this work items and not filter them on the server side
            matchingIds = isIncoming ? this.orderedIncomingIds : this.orderedOutgoingIds;
        } else {
            const pageableIds = new Set(isIncoming ? this.orderedIncomingIds : this.orderedOutgoingIds);
            matchingIds =
                textFilter || (this.serverSideFilteringByKeywordEnabled && !isFilterStateEmpty)
                    ? await this.getMatchingWorkItemIds(projectId, pageableIds, filterState, textFilter, column.name)
                    : [];
        }

        if (isIncoming) {
            this.lastIncomingFiltersStr = filtersStr;
            this.lastIncomingFilteredPagedIndex = -1; // We have not yet begun paging through these IDs
            this.idsForLastIncomingFilter = matchingIds;
        } else {
            this.lastOutgoingFiltersStr = filtersStr;
            this.lastOutgoingFilteredPagedIndex = -1; // We have not yet begun paging through these IDs
            this.idsForLastOutgoingFilter = matchingIds;
        }
    }

    /**
     * Retrieves a list of work item IDs which match the given filter and are members of the current board
     */
    private async getMatchingWorkItemIds(
        projectId: string,
        idsToPage: Set<number>,
        filterState: FilterState,
        filter?: string,
        columnName?: string
    ): Promise<number[]> {
        const idsAlreadyInTable = this.workItemService.getAllItems().map(item => item.value.id.toString());
        // No need to fetch more, since a perfect match is already in the board
        if (!this.serverSideFilteringByKeywordEnabled && idsAlreadyInTable.some(id => id === filter)) {
            return [];
        }

        const witClient = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        const query = this.serverSideFilteringByKeywordEnabled
            ? await this.buildWiqlForFilters(idsAlreadyInTable, filterState, filter, columnName)
            : this.buildWiqlForTextFilterObsolete(filter!, idsAlreadyInTable);
        const queryResult = await witClient.queryByWiql({ query }, projectId);

        // only return ids that are in the list of ids to page
        return queryResult.workItems.map(w => w.id).filter(id => idsToPage.has(id));
    }

    /**
     * Generates a WIQL query to fetch the IDs of work items matching the given filter
     * This method will be used only if "WebAccess.WorkItemTracking.ServerSideFilteringByKeywordDisabled" FF is true
     * Otherwise use buildWiqlForFilters instead
     */
    private buildWiqlForTextFilterObsolete(filter: string, idsToExclude: string[]): string {
        const idClause = isPositiveInteger(filter) ? `OR [System.Id] = ${filter}` : "";
        return `
            SELECT [System.Id]
            FROM WorkItems
            WHERE [System.TeamProject] = @project
                AND [System.Id] NOT IN (${idsToExclude.join(",")})
                AND (
                    [System.Title] = '${filter}'
                    ${idClause}
                )`;
    }

    /**
     * Generates a WIQL query to fetch the IDs of work items matching to the all active filters
     */
    private async buildWiqlForFilters(idsToExclude: string[], filterState: FilterState, textFilter?: string, columnName?: string): Promise<string> {
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings();
        const additionalConditions: string[] = [];

        const filterByColumnName = columnName ? `AND [${CoreFieldRefNames.BoardColumn}] = '${columnName}'` : "";

        const filterByItemTypes = this.boardSettingsDataObservable.value
            ? `AND [${CoreFieldRefNames.WorkItemType}] IN (${this.boardSettingsDataObservable.value.boardData.enabledWorkItemTypes
                    .map(type => `'${type}'`)
                    .join(",")})`
            : "";

        additionalConditions.push(filterByColumnName, filterByItemTypes);

        if (textFilter) {
            additionalConditions.push(this.buildWiqlConditionsForTextFilter(textFilter));
        }

        if (Object.keys(filterState).length > 0) {
            const subAreaPaths = await this.getSubPathsForAreaPathsWithIncludedSubAreas(filterState[CoreFieldRefNames.AreaPath], teamSettings);
            additionalConditions.push(this.buildWiqlConditionsForFilters(filterState, { teamSettings, subAreaPaths }));
        }

        return await this.aggregateWiqlForFilters(
            idsToExclude,
            additionalConditions,
            teamSettings,
            // We need to skip building area paths if filterState already has area paths to avoid duplicating the same paths
            !!filterState[CoreFieldRefNames.AreaPath] && this.additionalWiqlConditionsEnabled
        );
    }

    private async aggregateWiqlForFilters(
        idsToExclude: string[],
        additionalConditions: string[],
        teamSettings: ITeamSettings | undefined,
        skipAreaPath?: boolean
    ): Promise<string> {
        const areaPaths = teamSettings?.teamFieldValues;

        const buildAreaPathPredicate = (areaPath: ITeamFieldData): string => {
            const operator = areaPath.includeChildren ? "UNDER" : "=";
            return `[${CoreFieldRefNames.AreaPath}] ${operator} '${areaPath.value}'`;
        };

        const filterByAreaPaths =
            !skipAreaPath && areaPaths && areaPaths.length <= this.areaPathsWiqlFilterLimit
                ? `AND (${areaPaths.map((path, index) => `${index > 0 ? "OR " : ""}${buildAreaPathPredicate(path)}`).join(" ")})`
                : "";

        return `
            SELECT [${CoreFieldRefNames.Id}]
            FROM WorkItems
            WHERE [${CoreFieldRefNames.TeamProject}] = @project ${filterByAreaPaths}
                AND [${CoreFieldRefNames.Id}] NOT IN (${idsToExclude.join(",")})
                ${additionalConditions.join(" ")}`;
    }

    /**
     * Generates a WIQL query conditions to fetch the IDs of work items matching the given keyword filter
     */
    private buildWiqlConditionsForTextFilter(textFilter: string): string {
        const idClause = isPositiveInteger(textFilter) ? `OR [${CoreFieldRefNames.Id}] = ${textFilter}` : "";
        return `
                AND (
                    [${CoreFieldRefNames.Title}] CONTAINS '${textFilter}'
                    OR [${CoreFieldRefNames.Tags}] CONTAINS '${textFilter}'
                    OR [${CoreFieldRefNames.AssignedTo}] CONTAINS '${textFilter}'
                    OR [${CoreFieldRefNames.State}] CONTAINS '${textFilter}'
                    OR [${CoreFieldRefNames.WorkItemType}] CONTAINS '${textFilter}'
                    ${idClause}
                )`;
    }

    /**
     * Generates a WIQL query conditions to fetch the IDs of work items matching the regular filters (all filters exept keyword text)
     */
    private buildWiqlConditionsForFilters(
        filterState: FilterState,
        utils: { teamSettings: ITeamSettings | undefined; subAreaPaths?: IListBoxItem<WorkItemClassificationNode>[] }
    ): string {
        const queries: string[] = [];
        for (const key in filterState) {
            const query = this.buildWiqlConditionsForField(key, filterState[key], utils);
            queries.push(query);
        }
        return queries.join(" ");
    }

    private buildWiqlConditionsForField(
        refName: string,
        filterItemState: IFilterItemState,
        utils: { teamSettings: ITeamSettings | undefined; subAreaPaths?: IListBoxItem<WorkItemClassificationNode>[] }
    ): string {
        let operator = "CONTAINS";
        let allowedValues = filterItemState.values;
        if (
            refName === CoreFieldRefNames.AreaPath ||
            refName === CoreFieldRefNames.IterationPath ||
            refName === CoreFieldRefNames.Parent ||
            refName === CoreFieldRefNames.AssignedTo
        ) {
            operator = "=";
            if (refName === CoreFieldRefNames.AreaPath) {
                const teamPaths = utils.teamSettings?.teamFieldValues;
                if (teamPaths && teamPaths.length > 0) {
                    allowedValues = [];
                    if (this.additionalWiqlConditionsEnabled) {
                        filterItemState.values.forEach(areaPathLeaf => {
                            const resolvedPaths = getAreaPathsMatchingLeaf(
                                teamPaths.map(p => p.value),
                                areaPathLeaf.toString()
                            );
                            allowedValues.push(...resolvedPaths);
                        });

                        if (utils.subAreaPaths?.length) {
                            allowedValues.push(...utils.subAreaPaths.map(path => path.text).filter((text): text is string => text !== undefined));
                        }
                    } else {
                        if (!teamPaths.some(path => path.includeChildren)) {
                            filterItemState.values.map(areaPathLeaf => {
                                const resolvedPaths = getAreaPathsMatchingLeaf(
                                    teamPaths.map(p => p.value),
                                    areaPathLeaf.toString()
                                );
                                allowedValues.push(...resolvedPaths);
                            });
                        } else if (utils.subAreaPaths?.length) {
                            allowedValues.push(...utils.subAreaPaths.map(path => path.text!));
                        }
                    }
                }
            }
        }
        return allowedValues.length > 0
            ? `
                    AND (${allowedValues
                .map(
                    (value, index) =>
                        `${index > 0 ? (filterItemState.options?.useAndOperator ? "AND " : "OR ") : ""
                        }[${refName}] ${operator} ${this.getFilterItemValue(value)}`
                )
                .join(" ")})
                `
            : "";
    }

    private getFilterItemValue(value: FilterValue): string {
        value = value as string;
        if (value.startsWith("@")) {
            return value;
        } else if (value.startsWith("_") && value.endsWith("_")) {
            return "''";
        } else {
            return `'${value}'`;
        }
    }

    /**
     * Returns an array of the IDs of the next page items from the source based on currently paged index.
     * If there are IDs matching the current filter, these will appear first in the paging in order to
     * optimize cases where a simple filter on the ID or title is being applied to a large column.
     */
    private getNextPageIds(columnType: BoardColumnType, pageSize: number): number[] {
        const existingIds = new Set(this.workItemService.getAllItems().map(item => item.value.id));
        if (BoardColumnType.Incoming === columnType) {
            let firstIndexToPage = this.lastIncomingFilteredPagedIndex + 1;
            const idsFromFilter = this.idsForLastIncomingFilter.slice(firstIndexToPage, firstIndexToPage + pageSize);
            this.lastIncomingFilteredPagedIndex += idsFromFilter.length;
            firstIndexToPage = this.nextIncomingIndexToPage;
            // If filter is active, load only work items matching to the filter
            if (this.filterManager.isFiltering() && this.serverSideFilteringByKeywordEnabled) {
                this.nextIncomingIndexToPage = this.lastIncomingFilteredPagedIndex + 1;
                return idsFromFilter;
            } else {
                const numToTakeFromQueue = this.serverSideFilteringByKeywordEnabled ? pageSize : pageSize - idsFromFilter.length;
                const { result, endIndex } = sliceWithFilter(
                    this.orderedIncomingIds,
                    firstIndexToPage,
                    numToTakeFromQueue,
                    item => !existingIds.has(item)
                );
                this.nextIncomingIndexToPage = endIndex + 1;
                return this.serverSideFilteringByKeywordEnabled ? result : [...idsFromFilter, ...result];
            }
        } else if (BoardColumnType.Outgoing === columnType) {
            let firstIndexToPage = this.lastOutgoingFilteredPagedIndex + 1;
            const idsFromFilter = this.idsForLastOutgoingFilter.slice(firstIndexToPage, firstIndexToPage + pageSize);
            this.lastOutgoingFilteredPagedIndex += idsFromFilter.length;
            firstIndexToPage = this.nextOutgoingIndexToPage;
            if (this.filterManager.isFiltering() && this.serverSideFilteringByKeywordEnabled) {
                this.nextOutgoingIndexToPage = this.lastOutgoingFilteredPagedIndex + 1;
                return idsFromFilter;
            } else {
                const numToTakeFromQueue = this.serverSideFilteringByKeywordEnabled ? pageSize : pageSize - idsFromFilter.length;
                const { result, endIndex } = sliceWithFilter(
                    this.orderedOutgoingIds,
                    firstIndexToPage,
                    numToTakeFromQueue,
                    item => !existingIds.has(item)
                );
                this.nextOutgoingIndexToPage = endIndex + 1;
                return this.serverSideFilteringByKeywordEnabled ? result : [...idsFromFilter, ...result];
            }
        } else {
            return [];
        }
    }

    private insertWorkItemIntoColumn(column: BoardColumnLayout, workItem: IObservableBoardsWorkItem, previousId?: number, nextId?: number) {
        // remove the work item if it's already in this column
        const updatedWorkItems = column.workItems.value.filter(item => item.value.id !== workItem.value.id);
        if (previousId) {
            const previousIdIndex = updatedWorkItems.findIndex(workItem => workItem.value.id === previousId);
            if (previousIdIndex > -1) {
                updatedWorkItems.splice(previousIdIndex + 1, 0, workItem);
                column.workItems.value = updatedWorkItems;
            }
        } else if (nextId) {
            // if no previousId is given, insert at the start of the list
            updatedWorkItems.unshift(workItem);
            column.workItems.value = updatedWorkItems;
        } else {
            column.workItems.value = [...updatedWorkItems, workItem];
            this.sortColumn(column);
        }
    }

    /**
     * Check if a work item's current placement on the board is consistent with it's
     * field values. Used when we need to trigger a layout update after a work item's
     * state is changed from via some mechanism outside the board, e.g. the WIT form.
     */
    private async checkAndUpdateWorkItemLayout(item: IObservableBoardsWorkItem): Promise<void> {
        // does the work item still belong on the board?
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings();
        if (teamSettings) {
            const areaPath = item.value.getFieldValue<string>(CoreFieldRefNames.AreaPath) ?? "";
            const workItemType = item.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType) ?? "";

            const isTypeOnBacklog = this.boardSettingsData.value?.boardData.boardModel.itemTypes.includes(workItemType);
            const isAreaPathValid = teamSettings.teamFieldValues.some(teamFieldValue =>
                isClassificationPathEqualOrUnderRelative(areaPath, teamFieldValue.value, !!teamFieldValue.includeChildren)
            );
            if (!isTypeOnBacklog || !isAreaPathValid) {
                this.removeItem(item.value.id);
                return;
            }
            const { columnValue } = this.getWorkItemLayoutFieldValues(item.value);
            if (!columnValue) {
                // The work item doesn't map to a column on this board
                // Get the updated work item in case the new state does map to a column on the board
                const updatedItem = await this.getBoardWorkItem(item.value.id);
                if (updatedItem) {
                    const updatedLayoutFieldValues = this.getWorkItemLayoutFieldValues(updatedItem);
                    if (updatedLayoutFieldValues.columnValue) {
                        // Only set the item value if the new state maps to a column on the board, otherwise do nothing
                        // Updating the item value again if the new state did not map to a column on the board would cause an infinite loop
                        // where we continue to load the work item indenitely which will eventually lead to the user being throttled by the backend
                        item.value = updatedItem;
                    }
                }
            }
        }

        if (await this.swimlaneRulesAutoMoveService?.tryAutoMove(item.value)) {
            try {
                item.saving();
                if (await item.value.isValid(this.pageContext)) {
                    await item.value.save(this.pageContext);
                }
            } catch (err) {
                traceException(this.pageContext, "WIT", "KanbanBoard.AutoMoveItem", err);
            } finally {
                item.saveComplete();
            }
        }

        // is the work item still in the correct column/row?
        const columnLayout = this.getWorkItemColumnLayout(item.value.id);
        if (!columnLayout || !hasItem(columnLayout, item.value.id)) {
            // item isn't in the expected column
            this.updateBoardLayout();
        }
    }

    private async getBoardWorkItem(id: number): Promise<IBoardsWorkItem | undefined> {
        const boardData = this.boardSettingsData.value?.boardData;
        if (boardData) {
            const workItemData = this.getWorkItemSource(boardData);
            const extensionId = boardData.boardModel.boardSettings.extensionId;
            const fieldDefinitions = getFieldEntriesFromBoardFields(workItemData.fieldDefinitions);

            const workItems = await this.workItemService.loadWorkItems(this.projectId, [id], fieldDefinitions, extensionId);
            if (workItems && workItems.length > 0) {
                return workItems[0];
            }
        }
    }

    private getWorkItemLayoutFieldValues(item: IBoardsWorkItem): {
        columnValue: string | undefined;
        rowValue: string | undefined;
        doneValue: boolean | undefined;
    } {
        if (!this.boardFields) {
            return { columnValue: undefined, rowValue: undefined, doneValue: undefined };
        }

        const { columnField, doneField, rowField } = this.boardFields;
        const columnValue = item.getFieldValue(columnField) as string | undefined;
        const rowValue = item.getFieldValue(rowField) as string | undefined;
        const doneValue = !!item.getFieldValue<boolean>(doneField);
        return { columnValue, rowValue, doneValue };
    }

    private onFilterChanged = () => {
        this.filterManager.setFilters(mapToFilterState(this.filter.getState()));
        if (this.serverSideFilteringByKeywordEnabled) {
            // If filters are changed, user should be able to filter again
            const filterState = this.getFilterStateForWIQL();
            const textFilter = this.getTextFilter();
            const filtersStr = this.getFiltersString(filterState, textFilter);
            if (filtersStr !== this.filtersString) {
                this.tryToMakeVisibleShowMoreItemsBtn();
                this.filtersString = filtersStr;
            }
        }
        this.ignoredForFilters.clear();
        this.updateFilteredItems();
        this.isFilteringObservable.value = this.filterManager.isFiltering();
    };

    private onReorderFailure = (ex: any) => {
        const message = ex?.message as string | undefined;
        if (message) {
            const messageService = getBoardsMessagesService(this.pageContext);
            messageService.showMessage({
                renderMessage: () => <span>{message}</span>,
                severity: MessageCardSeverity.Error,
                containerName: ContainerNames.BoardTab
            });
        }
    };

    private workItemServiceListener = (value: IClientWorkItemUpdatedEvent<IClientWorkItem>, action?: ClientWorkItemServiceEvent | undefined) => {
        if (!value.item) {
            return;
        }

        const item = this.workItemService.get(value.item.id);
        if (!item) {
            if (action === ClientWorkItemServiceEvent.WorkItemRemoved) {
                // This is only used when dismissing a new invalid item so use the default column
                const defaultColumn = this.boardLayout.value?.columns.find(column => isIncoming(column));
                if (defaultColumn) {
                    defaultColumn.workItems.removeAll(item => item.value.id === value.item?.id);
                }
            }

            return;
        }

        item.updateBackgroundColor(this.pageContext, this.cardStyles);

        this.filterManager.dataUpdated();
        this.columnFilterManager.dataUpdated();

        if (action === ClientWorkItemServiceEvent.WorkItemUpdated) {
            // add WI to ignored only if it's being created
            if (item.value.saving) {
                this.ignoredForFilters.add(item.value.id);
            }
            this.checkAndUpdateWorkItemLayout(item);
        }

        if (action === ClientWorkItemServiceEvent.NewWorkItemAdded) {
            this.ignoredForFilters.add(item.value.id);
            // update filtered ids and add the work item to the top of the first column
            this.updateFilteredItems();
            const defaultColumn = this.boardLayout.value?.columns.find(column => isIncoming(column));
            if (defaultColumn) {
                defaultColumn.workItems.value = [item, ...defaultColumn.workItems.value];
            }
        }
    };

    private tryToMakeVisibleShowMoreItemsBtn() {
        const existingIds = new Set(this.workItemService.getAllItems().map(item => item.value.id));
        // If some work item from expected list isn't fetched from server yet, "Show more items" button can be shown
        if (this.orderedIncomingIds.some(id => !existingIds.has(id))) {
            const incomingColumns = this.boardLayout.value?.columns?.filter(c => c.columnType === BoardColumnType.Incoming);
            incomingColumns && incomingColumns.forEach(c => (c.hasPartialItems!.value = true));
        }
        if (this.orderedOutgoingIds.some(id => !existingIds.has(id))) {
            const outgoingColumns = this.boardLayout.value?.columns?.filter(c => c.columnType === BoardColumnType.Outgoing);
            outgoingColumns && outgoingColumns.forEach(c => (c.hasPartialItems!.value = true));
        }
    }

    private hasPartialItems(column: BoardColumnLayout): boolean {
        let negativeValueCheck: boolean;
        if (isIncoming(column)) {
            // If all filter results fetched from server, hide "Show more items" button
            negativeValueCheck = this.nextIncomingIndexToPage !== -1;
            if (this.filterManager.isFiltering() && this.serverSideFilteringByKeywordEnabled) {
                return negativeValueCheck && this.nextIncomingIndexToPage < this.idsForLastIncomingFilter.length;
            } else {
                return negativeValueCheck && this.nextIncomingIndexToPage < this.orderedIncomingIds.length;
            }
        } else {
            negativeValueCheck = this.nextOutgoingIndexToPage !== -1;
            if (this.filterManager.isFiltering() && this.serverSideFilteringByKeywordEnabled) {
                return negativeValueCheck && this.nextOutgoingIndexToPage < this.idsForLastOutgoingFilter.length;
            } else {
                return negativeValueCheck && this.nextOutgoingIndexToPage < this.orderedOutgoingIds.length;
            }
        }
    }

    private getFiltersString(filterState: FilterState, textFilter?: string): string {
        return `${textFilter ?? ""}${JSON.stringify(filterState)}`;
    }

    private getFilterStateForWIQL(): FilterState {
        const filterState = this.filterManager.getFilters();
        delete filterState[TextFilterProvider.PROVIDER_TYPE];
        return filterState;
    }

    private async getSubPathsForAreaPathsWithIncludedSubAreas(
        filterState: IFilterItemState | undefined,
        teamSettings: ITeamSettings | undefined
    ): Promise<IListBoxItem<WorkItemClassificationNode>[]> {
        if (teamSettings?.projectId === undefined || !filterState || filterState.values.length === 0) {
            return [];
        }

        if (!this.areaPathList || !this.additionalWiqlConditionsEnabled) {
            const areaPathNodes = await this.workItemNodeService.getAreaPathNodes(teamSettings.projectId);
            if (!areaPathNodes) {
                return [];
            }

            this.areaPathList = convertNodesToListBoxItems(areaPathNodes);
        }

        const filteredPaths: IListBoxItem<WorkItemClassificationNode>[] = teamSettings.teamFieldValues
            .flatMap(value =>
                // It is necessary to check whether it is completely equal or this is the beginning of a longer path,
                // in order to avoid filtering paths starting the same way
                value.includeChildren
                    ? this.areaPathList.filter(
                          path => (!this.additionalWiqlConditionsEnabled && path.text === value.value) || path.text?.startsWith(value.value + "\\")
                      )
                    : []
            )
            .filter(path => filterState.values.some(filter => path.text?.endsWith("\\" + filter.toString())));

        return filteredPaths;
    }

    private isParentFieldShownOnCards(boardData?: BoardData): boolean {
        if (!boardData) {
            return false;
        }
        const cards = boardData.boardModel.boardCardSettings.cards;
        const workItemTypes = boardData.enabledWorkItemTypes;

        return workItemTypes.some(type => cards[type]?.some(field => field.fieldIdentifier === CoreFieldRefNames.Parent));
    }

    private async getPermissions(contributionService: IVssContributionService): Promise<void> {
        const workItemPermissions = await contributionService.getDataAsync<IWorkItemPermissionData>(WorkItemPermissionsDataProvider);
        this.hasPermissionToDeleteWorkItem.value = workItemPermissions?.workItemDeletePermission?.hasPermission ?? false;
    }
}

function convertBoardFieldDefinitionToFieldLayout(bfd: BoardFieldDefinition): FieldLayout {
    return {
        displayName: bfd.Name,
        referenceName: bfd.ReferenceName,
        isIdentity: bfd.IsIdentity,
        isEditable: bfd.IsEditable
    };
}

function convertCardRulesToTagStyleMap(tagStyles: CardRule[]): Map<string, string> {
    const tagStylesMap = new Map<string, string>();
    for (const tagStyle of tagStyles) {
        if (!tagStyle.isEnabled) {
            continue;
        }

        const tagColor = (tagStyle.style as any)["background-color"];
        if (tagColor) {
            tagStylesMap.set(tagStyle.name, tagColor);
        }
    }

    return tagStylesMap;
}

function getFilterableFields(boardData?: BoardData): string[] {
    if (!boardData) {
        return [];
    }

    const filterableFields = new Set<string>();
    const filterableFieldsByType = boardData.boardModel.board.filterableFieldNamesByItemType;
    for (const type of Object.keys(filterableFieldsByType)) {
        filterableFieldsByType[type].forEach(field => filterableFields.add(field));
    }

    return [...filterableFields];
}

function isPositiveInteger(str: string): boolean {
    const n = Math.floor(Number(str));
    return isFinite(n) && n > 0 && String(n) === str;
}

function isTopBacklogLevel(backlogLevelName: string, backlogConfiguration: BacklogConfiguration): boolean {
    const backlogLevels = [...backlogConfiguration.portfolioBacklogs, backlogConfiguration.requirementBacklog, backlogConfiguration.taskBacklog];
    const currentLevel = backlogLevels.find(backlogLevel => backlogLevel.id === backlogLevelName);
    const maxRank = Math.max(...backlogLevels.map(level => level.rank));
    return !!currentLevel && currentLevel.rank === maxRank;
}

function sanitizeFilter(filter: string): string {
    filter = filter.trim();
    while (filter.indexOf("'") > 0) {
        filter = filter.replace("'", " ");
    }

    return filter;
}

/**
 * Slice an array from a starting index up to a number of items matching the given filter
 */
function sliceWithFilter<T>(list: T[], start: number, length: number, filter: (item: T) => boolean): { result: T[]; endIndex: number } {
    const result: T[] = [];
    let endIndex = -1;
    if (length > 0) {
        for (let i = start; i < list.length; i++) {
            const item = list[i];
            if (filter(item)) {
                result.push(item);
                if (result.length === length) {
                    break;
                }
            }

            endIndex = i;
        }
    }

    return { result, endIndex };
}

const serviceName = "IKanbanBoardService";
Services.add(serviceName, { serviceFactory: KanbanBoardService });
